import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useState } from 'react'

import {
   ADMIN_ROUTE_PATH,
   ADMIN_ROUTE_SUFFIX,
   useGoogleCalendarAuthorization
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import { isGoogleCalendarIntegrationEnabled } from '@flowwai-frontend/bps-officer-web/common/ui'
import { Config } from '@flowwai-frontend/bps-officer-web/env-config'
import {
   useClientConfig,
   useUserAppConfigStore,
   useUserAttendanceStatus
} from '@flowwai-frontend/bps-officer-web/iam'
import { LogoutController } from '@flowwai-frontend/bps-officer-web/login'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { AvatarLabelGroup, Popover } from '@flowwai-frontend/design-system'
import { isAPIFetching } from '@flowwai-frontend/shared/api-common'
import { Loader, useCustomT } from '@flowwai-frontend/shared/common-components'
import {
   AlertOctagonIcon,
   DotsHorizontal,
   GoogleCalendarIcon,
   HelpCircleIcon,
   LinkExternalIcon,
   MessageDotsIcon,
   PlusIcon,
   UserCircleIcon,
   XCloseIcon
} from '@flowwai-frontend/design-system/icons'
import { useAccountStore } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import ClockOutController from '../../controllers/ClockOutController/ClockOutController'
import OptionItem from '../OptionItem/OptionItem'
import LastClockInSection from './LastClockInSection'
import {
   adminAndFeedbackSectionClassName,
   getTriggerStyles,
   googleCalendarSectionContainerClassName,
   logoutSectionContainerClassName,
   popoverContentClassName,
   userDetailsSectionClassName
} from './styles'

interface FormQueryFieldIds {
   accountId: string
   accountName: string
   userId: string
   userName: string
   userEmail: string
}

interface Props {
   shouldShowAdminNavigationOption: boolean
}

const NavFooterPopover = (props: Props): React.ReactElement => {
   const [isOpen, setIsOpen] = useState(false)
   const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)
   const [isClockOutModalOpen, setIsClockOutModalOpen] = useState(false)

   const { theme } = useAppTheme()
   const { footerPopover: navTheme } = theme.navbar
   const t = useCustomT('navbar:footer')
   const commonT = useCustomT('navbar:common')

   const { enableSupportRequest } = useClientConfig()
   const userStore = useUserAppConfigStore()
   const { userDetails } = userStore
   const accountStore = useAccountStore()

   const userAppConfigStore = useUserAppConfigStore()
   const isAdmin = userDetails?.isAdmin
   const isAdminRoute = window.location.pathname.includes(ADMIN_ROUTE_SUFFIX)
   const shouldShowAdminOptionItem =
      !isAdminRoute && isAdmin && props.shouldShowAdminNavigationOption

   const isConnectedToGoogleCalendar =
      userStore.userGoogleCalenderAuthConsentStatus === 'AUTHORIZATION_GRANTED'

   const showGoogleCalendarSection =
      isGoogleCalendarIntegrationEnabled &&
      userAppConfigStore.permissions?.isSchedulingEnabled &&
      userAppConfigStore.permissions?.isGoogleCalendarIntegrationEnabled

   const {
      connectToGoogleCalendar,
      disconnectFromGoogleCalendar,
      apiStatus: googleCalendarAuthApiStatus
   } = useGoogleCalendarAuthorization({
      setGoogleAuthStatus: userStore.setUserGoogleCalenderAuthConsentStatus
   })

   const { getAttendanceStatus } = useUserAttendanceStatus()

   const onClickLogout = (): void => {
      setIsLogoutModalOpen(true)
      setIsOpen(false)
   }

   const onClickClockOut = (): void => {
      setIsClockOutModalOpen(true)
   }

   const renderLogoutSection = (): React.ReactElement => (
      <div className={cn(logoutSectionContainerClassName, navTheme.border)}>
         <OptionItem
            name={t('logOut')}
            rightEnhancer={() => (
               <svg width={16} height={16} fill='none'>
                  <use xlinkHref='/assets/icons/iconsSprite.svg#logout1' />
               </svg>
            )}
            onAction={onClickLogout}
         />
      </div>
   )

   const onClickGoogleCalendar = (): void => {
      if (isConnectedToGoogleCalendar) disconnectFromGoogleCalendar()
      else connectToGoogleCalendar()
   }

   const renderGoogleCalendarActionIcon = (): React.ReactElement => {
      if (isAPIFetching(googleCalendarAuthApiStatus))
         return <Loader width={16} height={16} />
      return isConnectedToGoogleCalendar ? (
         <XCloseIcon height={16} width={16} fill={navTheme.closeIconColor} />
      ) : (
         <PlusIcon height={16} width={16} fill={navTheme.plusIconColor} />
      )
   }

   const renderGoogleCalendarSection = (): React.ReactElement => (
      <div
         className={cn(
            googleCalendarSectionContainerClassName,
            navTheme.border
         )}
      >
         <OptionItem
            name={
               isConnectedToGoogleCalendar
                  ? t('disconnectCalendar')
                  : t('connectCalendar')
            }
            rightEnhancer={() => <GoogleCalendarIcon width={20} height={20} />}
            leftEnhancer={renderGoogleCalendarActionIcon}
            onAction={onClickGoogleCalendar}
         />
      </div>
   )

   const openFormURL = (formURL: string, queryConfig: FormQueryFieldIds) => {
      const url = new URL(formURL)
      const searchParams = url.searchParams

      const {
         accountId,
         accountName,
         userId,
         userName,
         userEmail
      } = queryConfig

      searchParams.append(accountId, accountStore?.accountId || '')
      searchParams.append(accountName, accountStore?.accountName || '')
      searchParams.append(userId, userDetails?.userId || '')
      searchParams.append(userName, userDetails?.name || '')
      searchParams.append(userEmail, userDetails?.email || '')

      window.open(url.toString(), '_blank')
   }

   const onClickIssues = (): void => {
      const issuesURL = Config.ISSUES_FORM_URL
      if (!issuesURL) return

      const queryConfig: FormQueryFieldIds = {
         accountId: Config.ISSUES_FORM_QUERY_ACCOUNT_ID,
         accountName: Config.ISSUES_FORM_QUERY_ACCOUNT_NAME,
         userId: Config.ISSUES_FORM_QUERY_USER_ID,
         userName: Config.ISSUES_FORM_QUERY_USER_NAME,
         userEmail: Config.ISSUES_FORM_QUERY_USER_EMAIL
      }

      openFormURL(issuesURL, queryConfig)
   }

   const onClickFeedback = (): void => {
      const feedbackFormURL = Config.FEEDBACK_FORM_URL
      if (!feedbackFormURL) return

      const queryConfig: FormQueryFieldIds = {
         accountId: Config.FEEDBACK_FORM_QUERY_ACCOUNT_ID,
         accountName: Config.FEEDBACK_FORM_QUERY_ACCOUNT_NAME,
         userId: Config.FEEDBACK_FORM_QUERY_USER_ID,
         userName: Config.FEEDBACK_FORM_QUERY_USER_NAME,
         userEmail: Config.FEEDBACK_FORM_QUERY_USER_EMAIL
      }

      openFormURL(feedbackFormURL, queryConfig)
   }

   const onClickSupportRequest = (): void => {
      const supportRequestURL = Config.SUPPORT_REQUEST_FORM_URL
      if (!supportRequestURL) return

      const queryConfig: FormQueryFieldIds = {
         accountId: Config.SUPPORT_REQUEST_FORM_QUERY_ACCOUNT_ID,
         accountName: Config.SUPPORT_REQUEST_FORM_QUERY_ACCOUNT_NAME,
         userId: Config.SUPPORT_REQUEST_FORM_QUERY_USER_ID,
         userName: Config.SUPPORT_REQUEST_FORM_QUERY_USER_NAME,
         userEmail: Config.SUPPORT_REQUEST_FORM_QUERY_USER_EMAIL
      }

      openFormURL(supportRequestURL, queryConfig)
   }

   const onClickAdminSettings = (): void => {
      window.open(`${ADMIN_ROUTE_PATH}`, '_blank')

      setIsOpen(false)
   }

   const renderAdminAndFeedbackSection = (): React.ReactElement => (
      <div
         className={cn(adminAndFeedbackSectionClassName, navTheme.borderColor)}
      >
         {shouldShowAdminOptionItem ? (
            <OptionItem
               name={t('adminSettings')}
               rightEnhancer={() => (
                  <UserCircleIcon
                     width={16}
                     height={16}
                     fill={navTheme.userCircleIconColor}
                  />
               )}
               leftEnhancer={() => (
                  <LinkExternalIcon
                     width={18}
                     height={18}
                     fill={navTheme.linkExternalIconColor}
                  />
               )}
               onAction={onClickAdminSettings}
            />
         ) : null}
         {enableSupportRequest ? (
            <>
               {isAdmin ? (
                  <OptionItem
                     name={t('supportRequest')}
                     rightEnhancer={() => (
                        <HelpCircleIcon
                           width={16}
                           height={16}
                           fill={navTheme.helpCircleIconColor}
                        />
                     )}
                     onAction={onClickSupportRequest}
                  />
               ) : null}
               <OptionItem
                  name={t('feedback')}
                  rightEnhancer={() => (
                     <MessageDotsIcon
                        width={16}
                        height={16}
                        fill={navTheme.messageDotsIconColor}
                     />
                  )}
                  onAction={onClickFeedback}
               />
               <OptionItem
                  name={t('issues')}
                  rightEnhancer={() => (
                     <AlertOctagonIcon
                        width={16}
                        height={16}
                        fill={navTheme.alertOctagonIconColor}
                     />
                  )}
                  onAction={onClickIssues}
               />
            </>
         ) : null}
      </div>
   )

   const renderUserDetails = (): React.ReactElement | null => {
      if (!userDetails) return null

      return (
         <div className='flex-grow truncate'>
            <AvatarLabelGroup
               description={userDetails.email || ''}
               name={userDetails.name || ''}
               type={userDetails.profilePicUrl ? 'Image' : 'Text'}
               shape={'Circular'}
               size={'Medium'}
               imageURL={userDetails.profilePicUrl || ''}
               labelClassName={'truncate'}
               labelDescriptionClassName={'truncate'}
               status={getAttendanceStatus(userDetails)}
            />
         </div>
      )
   }

   const renderUserDetailsSection = (): React.ReactElement | null => {
      if (!userDetails) return null

      return (
         <div className={userDetailsSectionClassName}>
            {renderUserDetails()}
         </div>
      )
   }

   const hasUserDetailsSection =
      shouldShowAdminOptionItem || enableSupportRequest

   const renderPopoverContent = (): React.ReactElement => (
      <div className={cn(popoverContentClassName, navTheme.borderColor)}>
         {renderUserDetailsSection()}
         {userAppConfigStore.permissions.isAttendanceTrackingEnabled ? (
            <LastClockInSection onClickClockOut={onClickClockOut} />
         ) : null}
         {hasUserDetailsSection ? renderAdminAndFeedbackSection() : null}
         {showGoogleCalendarSection ? renderGoogleCalendarSection() : null}
         {renderLogoutSection()}
      </div>
   )

   const renderTrigger = (): React.ReactElement => (
      <div
         className={getTriggerStyles(isOpen, navTheme)}
         title={commonT('more')}
      >
         <DotsHorizontal
            width={20}
            height={20}
            fill={navTheme.dotsHorizontalIconColor}
         />
      </div>
   )

   return (
      <>
         <Popover
            renderPopoverTrigger={renderTrigger}
            renderPopoverBody={renderPopoverContent}
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            offset={12}
            placement={'top start'}
         />
         <LogoutController
            isLogoutModalOpen={isLogoutModalOpen}
            setIsLogoutModalOpen={setIsLogoutModalOpen}
         />
         <ClockOutController
            isClockOutModalOpen={isClockOutModalOpen}
            setIsClockOutModalOpen={setIsClockOutModalOpen}
         />
      </>
   )
}

export default observer(NavFooterPopover)
